import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import MailIcon from '@mui/icons-material/Mail';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WcIcon from '@mui/icons-material/Wc';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import toast from 'react-hot-toast';
import MainContent from '../../components/MainContent';
import Spinner from '../../components/spinner/Spinner';
import { axiosInstancev2 } from '../../utils/Axios/axiosInterceptorv2';

const CMS_BASE_URL = 'https://cms.powerlearnprojectafrica.org';

function HPFAssessmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [q1Score, setQ1Score] = useState(0);
  const [q1Feedback, setQ1Feedback] = useState('');
  const [q2aScore, setQ2aScore] = useState(0);
  const [q2aFeedback, setQ2aFeedback] = useState('');
  const [q2bScore, setQ2bScore] = useState(0);
  const [q2bFeedback, setQ2bFeedback] = useState('');
  const [q3aScore, setQ3aScore] = useState(0);
  const [q3aFeedback, setQ3aFeedback] = useState('');
  const [q3bScore, setQ3bScore] = useState(0);
  const [q3bFeedback, setQ3bFeedback] = useState('');
  const [overallFeedback, setOverallFeedback] = useState('');

  const fetchSubmission = async () => {
    const response = await axiosInstancev2.get(
      `/cms/strapi/hpf-assessment-submissions/${id}?populate=*`
    );
    const { data } = response.data;
    const attr = data.attributes || data;

    // Initialize form state with fetched data
    setQ1Score(attr.q1Score || 0);
    setQ1Feedback(attr.q1Feedback || '');
    setQ2aScore(attr.q2aScore || 0);
    setQ2aFeedback(attr.q2aFeedback || '');
    setQ2bScore(attr.q2bScore || 0);
    setQ2bFeedback(attr.q2bFeedback || '');
    setQ3aScore(attr.q3aScore || 0);
    setQ3aFeedback(attr.q3aFeedback || '');
    setQ3bScore(attr.q3bScore || 0);
    setQ3bFeedback(attr.q3bFeedback || '');
    setOverallFeedback(attr.overallFeedback || '');

    return data;
  };

  const {
    data: submission,
    isLoading,
    error,
  } = useQuery(['hpf-submission', id], fetchSubmission);

  const updateMutation = useMutation(
    async (updateData) => {
      return axiosInstancev2.put(
        `/cms/strapi/hpf-assessment-submissions/${id}`,
        {
          data: updateData,
        }
      );
    },
    {
      onSuccess: () => {
        toast.success('Assessment graded successfully!');
        queryClient.invalidateQueries(['hpf-submissions']);
        queryClient.invalidateQueries(['hpf-submission', id]);
        navigate('/hpf-assessment');
      },
      onError: (err) => {
        const errorMsg =
          err.response?.data?.error?.message ||
          err.response?.data?.error ||
          err.message;
        toast.error(`Failed to update assessment: ${errorMsg}`);
      },
    }
  );

  const handleSave = (e) => {
    e.preventDefault();

    const s1 = parseInt(q1Score, 10) || 0;
    const s2a = parseInt(q2aScore, 10) || 0;
    const s2b = parseInt(q2bScore, 10) || 0;
    const s3a = parseInt(q3aScore, 10) || 0;
    const s3b = parseInt(q3bScore, 10) || 0;

    if (s1 < 0 || s1 > 20) {
      toast.error('Q1 score must be 0-20');
      return;
    }
    if (s2a < 0 || s2a > 15) {
      toast.error('Q2 Part A score must be 0-15');
      return;
    }
    if (s2b < 0 || s2b > 10) {
      toast.error('Q2 Part B score must be 0-10');
      return;
    }
    if (s3a < 0 || s3a > 15) {
      toast.error('Q3 Part A score must be 0-15');
      return;
    }
    if (s3b < 0 || s3b > 15) {
      toast.error('Q3 Part B score must be 0-15');
      return;
    }

    const totalPractical = s1 + s2a + s2b + s3a + s3b;

    updateMutation.mutate({
      q1Score: s1,
      q1Feedback,
      q2aScore: s2a,
      q2aFeedback,
      q2bScore: s2b,
      q2bFeedback,
      q3aScore: s3a,
      q3aFeedback,
      q3bScore: s3b,
      q3bFeedback,
      practicalScore: totalPractical,
      overallFeedback,
      status: 'Graded',
      feedbackEmailSent: false, // Reset so it can be re-sent
    });
  };

  if (isLoading) {
    return (
      <MainContent full>
        <div className="flex h-[60vh] items-center justify-center">
          <Spinner />
        </div>
      </MainContent>
    );
  }

  if (error) {
    return (
      <MainContent full>
        <div className="p-6 text-red-500">Error: {error.message}</div>
      </MainContent>
    );
  }

  const attr = submission.attributes || submission;
  const mcqScore = attr.mcqScore || 0;
  const practicalScore =
    (parseInt(q1Score, 10) || 0) +
    (parseInt(q2aScore, 10) || 0) +
    (parseInt(q2bScore, 10) || 0) +
    (parseInt(q3aScore, 10) || 0) +
    (parseInt(q3bScore, 10) || 0);
  const totalScore = practicalScore + mcqScore;

  // Handle both old string format and new media field format
  let files = [];
  const rawFiles = attr.practicalTestFileSubmissionUrl;

  if (typeof rawFiles === 'string') {
    // Old string format: split by comma
    files = rawFiles
      .split(',')
      .map((url) => url.trim())
      .filter((url) => url && !url.includes('Multiple files uploaded'))
      .map((url) => ({
        url: url.startsWith('http') ? url : `${CMS_BASE_URL}${url}`,
        name: url.split('/').pop(),
      }));
  } else if (Array.isArray(rawFiles)) {
    // Strapi v5 flattened media array
    files = rawFiles.map((file) => ({
      url: file.url.startsWith('http')
        ? file.url
        : `${CMS_BASE_URL}${file.url}`,
      name: file.name || file.url.split('/').pop(),
    }));
  } else if (rawFiles && rawFiles.data) {
    // Strapi v4/v5 non-flattened media object
    const data = Array.isArray(rawFiles.data) ? rawFiles.data : [rawFiles.data];
    files = data.map((item) => {
      const file = item.attributes || item;
      return {
        url: file.url.startsWith('http')
          ? file.url
          : `${CMS_BASE_URL}${file.url}`,
        name: file.name || file.url.split('/').pop(),
      };
    });
  }

  return (
    <MainContent full>
      <div className="p-6 bg-[#F9FAFB] min-h-screen">
        <button
          type="button"
          onClick={() => navigate('/hpf-assessment')}
          className="flex items-center gap-2 text-gray-600 hover:text-[#009688] transition-colors mb-6 font-medium"
        >
          <ArrowBackIcon sx={{ fontSize: 20 }} />
          Back to Submissions
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Candidate Info & MCQ Results */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-8 rounded-[32px] shadow-sm border border-gray-100">
              <div className="flex flex-col items-center text-center mb-8">
                <div className="w-20 h-20 bg-[#009688]/10 rounded-3xl flex items-center justify-center text-[#009688] font-bold text-2xl mb-4">
                  {attr.fullName.charAt(0)}
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {attr.fullName}
                </h2>
                <p className="text-gray-500 flex items-center gap-1 mt-1">
                  <MailIcon sx={{ fontSize: 16 }} />
                  {attr.email}
                </p>
              </div>

              <div className="space-y-4 border-t border-gray-50 pt-6">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center gap-2">
                    <CalendarTodayIcon sx={{ fontSize: 16 }} />
                    Date of Birth
                  </span>
                  <span className="font-medium text-gray-900">
                    {new Date(attr.dateOfBirth).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center gap-2">
                    <WcIcon sx={{ fontSize: 16 }} />
                    Gender
                  </span>
                  <span className="font-medium text-gray-900 capitalize">
                    {attr.gender}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center gap-2">
                    <AssignmentIcon sx={{ fontSize: 16 }} />
                    Submission ID
                  </span>
                  <span className="font-mono text-xs bg-gray-50 px-2 py-1 rounded text-gray-600">
                    {id}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-[#009688] p-8 rounded-[32px] shadow-lg shadow-[#009688]/20 text-white">
              <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
                <CheckCircleIcon />
                MCQ Assessment Results
              </h3>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-5xl font-bold">{mcqScore}</span>
                <span className="text-xl opacity-80">/ 25</span>
              </div>
              <p className="text-sm opacity-90 mb-6">
                Candidate scored {Math.round((mcqScore / 25) * 100)}% in the
                multiple choice section.
              </p>
              <div className="w-full bg-white/20 h-2 rounded-full overflow-hidden">
                <div
                  className="bg-white h-full transition-all duration-1000"
                  style={{ width: `${(mcqScore / 25) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* Right Column: Practical Grading */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white p-8 rounded-[32px] shadow-sm border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Practical Submission Files
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {files.length > 0 ? (
                  files.map((file) => {
                    return (
                      <a
                        key={file.url}
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100 hover:border-[#009688] hover:bg-[#009688]/5 transition-all group"
                      >
                        <div className="flex items-center gap-3 overflow-hidden">
                          <div className="p-2 bg-white rounded-xl text-[#009688] shadow-sm">
                            <AssignmentIcon />
                          </div>
                          <div className="flex flex-col overflow-hidden">
                            {file.name.match(/^Q[1-3][a-b]?_/) && (
                              <span className="text-[10px] font-bold text-[#009688] uppercase tracking-wider">
                                Question {file.name.split('_')[0].substring(1)}
                              </span>
                            )}
                            <span className="text-sm font-medium text-gray-700 truncate">
                              {file.name.replace(/^Q[1-3][a-b]?_/, '')}
                            </span>
                          </div>
                        </div>
                        <DownloadIcon
                          className="text-gray-400 group-hover:text-[#009688]"
                          sx={{ fontSize: 20 }}
                        />
                      </a>
                    );
                  })
                ) : (
                  <div className="col-span-2 p-8 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200 text-gray-500">
                    No practical files submitted or files are in legacy format.
                  </div>
                )}
              </div>
            </div>

            <form
              onSubmit={handleSave}
              className="bg-white p-8 rounded-[32px] shadow-sm border border-gray-100 space-y-8"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-bold text-gray-900">
                  Grade Practical Section
                </h3>
                <div
                  className={`px-4 py-1 rounded-full text-xs font-bold border ${
                    attr.status === 'Graded'
                      ? 'bg-green-100 text-green-700 border-green-200'
                      : 'bg-yellow-100 text-yellow-700 border-yellow-200'
                  }`}
                >
                  {attr.status ? attr.status.toUpperCase() : 'PENDING'}
                </div>
              </div>

              {/* Question 1 */}
              <div className="space-y-4 p-6 bg-gray-50/50 rounded-2xl border border-gray-100">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <label className="text-sm font-bold text-gray-700">
                    Question 1 (Max 20 marks)
                  </label>
                  <div className="relative w-full md:w-32">
                    <input
                      type="number"
                      max="20"
                      min="0"
                      value={q1Score}
                      onChange={(e) => setQ1Score(e.target.value)}
                      className="w-full px-4 py-2 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#009688]/20 focus:border-[#009688] transition-all font-bold text-center"
                      required
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-medium">
                      / 20
                    </span>
                  </div>
                </div>
                <textarea
                  value={q1Feedback}
                  onChange={(e) => setQ1Feedback(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#009688]/20 focus:border-[#009688] transition-all text-sm min-h-[80px] resize-none"
                  placeholder="Feedback for Question 1 (Optional)"
                />
              </div>

              {/* Question 2 */}
              <div className="space-y-6 p-6 bg-gray-50/50 rounded-2xl border border-gray-100">
                <h4 className="text-sm font-bold text-gray-700 border-b border-gray-200 pb-2">
                  Question 2 (Total 25 marks)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold text-gray-600 uppercase">
                        Part A (Max 15)
                      </label>
                      <div className="relative w-24">
                        <input
                          type="number"
                          max="15"
                          min="0"
                          value={q2aScore}
                          onChange={(e) => setQ2aScore(e.target.value)}
                          className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#009688]/20 focus:border-[#009688] transition-all font-bold text-center text-sm"
                          required
                        />
                      </div>
                    </div>
                    <textarea
                      value={q2aFeedback}
                      onChange={(e) => setQ2aFeedback(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#009688]/20 focus:border-[#009688] transition-all text-xs min-h-[60px] resize-none"
                      placeholder="Part A Feedback"
                    />
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold text-gray-600 uppercase">
                        Part B (Max 10)
                      </label>
                      <div className="relative w-24">
                        <input
                          type="number"
                          max="10"
                          min="0"
                          value={q2bScore}
                          onChange={(e) => setQ2bScore(e.target.value)}
                          className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#009688]/20 focus:border-[#009688] transition-all font-bold text-center text-sm"
                          required
                        />
                      </div>
                    </div>
                    <textarea
                      value={q2bFeedback}
                      onChange={(e) => setQ2bFeedback(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#009688]/20 focus:border-[#009688] transition-all text-xs min-h-[60px] resize-none"
                      placeholder="Part B Feedback"
                    />
                  </div>
                </div>
              </div>

              {/* Question 3 */}
              <div className="space-y-6 p-6 bg-gray-50/50 rounded-2xl border border-gray-100">
                <h4 className="text-sm font-bold text-gray-700 border-b border-gray-200 pb-2">
                  Question 3 (Total 30 marks)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold text-gray-600 uppercase">
                        Part A (Max 15)
                      </label>
                      <div className="relative w-24">
                        <input
                          type="number"
                          max="15"
                          min="0"
                          value={q3aScore}
                          onChange={(e) => setQ3aScore(e.target.value)}
                          className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#009688]/20 focus:border-[#009688] transition-all font-bold text-center text-sm"
                          required
                        />
                      </div>
                    </div>
                    <textarea
                      value={q3aFeedback}
                      onChange={(e) => setQ3aFeedback(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#009688]/20 focus:border-[#009688] transition-all text-xs min-h-[60px] resize-none"
                      placeholder="Part A Feedback"
                    />
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold text-gray-600 uppercase">
                        Part B (Max 15)
                      </label>
                      <div className="relative w-24">
                        <input
                          type="number"
                          max="15"
                          min="0"
                          value={q3bScore}
                          onChange={(e) => setQ3bScore(e.target.value)}
                          className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#009688]/20 focus:border-[#009688] transition-all font-bold text-center text-sm"
                          required
                        />
                      </div>
                    </div>
                    <textarea
                      value={q3bFeedback}
                      onChange={(e) => setQ3bFeedback(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#009688]/20 focus:border-[#009688] transition-all text-xs min-h-[60px] resize-none"
                      placeholder="Part B Feedback"
                    />
                  </div>
                </div>
              </div>

              {/* Summary Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">
                    Total Practical Score
                  </label>
                  <div className="w-full px-4 py-3 bg-[#F0F9F9] border border-[#D1F2F2] rounded-2xl font-bold text-2xl text-[#009688] flex items-center justify-between">
                    <span>{practicalScore} / 75</span>
                    <span className="text-xs opacity-60">
                      {Math.round((practicalScore / 75) * 100)}%
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">
                    Final Combined Score
                  </label>
                  <div className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-2xl font-bold text-2xl text-white flex items-center justify-between">
                    <span>{totalScore}%</span>
                    <span
                      className={`text-xs px-3 py-1 rounded-lg ${
                        totalScore >= 70
                          ? 'bg-green-500 text-white'
                          : 'bg-red-500 text-white'
                      }`}
                    >
                      {totalScore >= 70 ? 'PASS' : 'FAIL'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-700">
                  Overall Feedback
                </label>
                <textarea
                  value={overallFeedback}
                  onChange={(e) => setOverallFeedback(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#009688]/20 focus:border-[#009688] transition-all min-h-[120px] resize-none"
                  placeholder="Provide final summary feedback..."
                />
              </div>

              <div className="pt-4 flex flex-col md:flex-row gap-4">
                <button
                  type="submit"
                  disabled={updateMutation.isLoading}
                  className="flex-1 bg-[#009688] hover:bg-[#00796B] text-white py-4 rounded-2xl font-bold shadow-lg shadow-[#009688]/20 transition-all hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {updateMutation.isLoading ? (
                    <Spinner size="sm" />
                  ) : (
                    <SaveIcon />
                  )}
                  {attr.status === 'Graded'
                    ? 'Update Grade'
                    : 'Submit Grade & Finalize'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </MainContent>
  );
}

export default HPFAssessmentDetail;
